import { useMemo, useRef, useState } from 'react';

import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarQuickFilter,
    useGridApiContext,
    useGridSelector,
    gridPageCountSelector,
    gridPaginationModelSelector,
    gridDensitySelector,
    GridViewHeadlineIcon,
    GridViewStreamIcon,
    GridTableRowsIcon,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { useSelector } from 'BMapsRedux';
import { isGroupEntry, getEntryMapCases, getTargetMapCases } from 'BMapsRedux/projectState/entry';
import { userConfirmation } from 'BMapsSrc/utils';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import wrapForDisabledTooltip from 'BMapsUI/common/helpers/wrapForDisabledTooltip';
import usePinPopoverBottom from 'BMapsUI/common/helpers/usePinPopoverBottom';
import { Menu, MenuItem } from '@mui/material';
import { EventBroker } from '../eventbroker';
import { DialogSubmitButton } from '../ui/common/DialogActionButtons';
import Select from '../ui/common/Select';
import { TextArea } from '../ui/common/TextFields';
import { WorkingIndicator } from '../ui/ui_utils';
import { MapCase } from '../model/MapCase';

const ClassicNoFragmentDataTooltip = 'Classic fragment search is only available for atoms from structures that already have fragment simulation data.';

const defaultMaxBValue = 0;
const defaultSearchRadius = 6;
// Selecting a curated panel can pull in many structures; querying each is a server
// round-trip, so confirm before running a query that spans more than this many.
const LargeQueryThreshold = 25;

const FragQueryDataStates = Object.freeze({
    NoProtein: 'NoProtein',
    Initializing: 'Initializing',
    NoDataAvailable: 'NoDataAvailable',
    DataAvailable: 'DataAvailable',
});

export default function FragDataMenuControl({
    fragData, initialFromAtom, initialToAtom,
    close=() => {},
}) {
    // Keep in sync with redux in case targets change while the panel is open
    const targetList = useSelector((state) => state.projectState.targetList);

    // For each project target, resolve the loaded CaseData (if any). Show ALL targets:
    // loaded structures without fragment data render as disabled (non-selectable) rows.
    // "Queryable" = loaded-with-fragments, or an unloaded structure of interest
    // (unknown availability, offered optimistically).
    const projectTargetItems = targetList.map((entry) => {
        if (isGroupEntry(entry)) {
            // A curated panel renders as ONE row whose checkbox toggles ALL its members into the
            // query. A member that is also added individually keeps its own row, but selecting
            // the library necessarily includes it — and its own row is then locked (see
            // TargetControl) — so members are NOT excluded here.
            return {
                id: entry.groupId,
                isGroup: true,
                mapCase: null,
                mapCases: getEntryMapCases(entry),
                displayName: entry.displayName,
                caseData: null,
            };
        }
        const caseData = App.Workspace.lookupCaseData(entry.mapCase);
        return {
            id: `${entry.mapCase.sourceDesc}:${entry.mapCase.uri ?? entry.mapCase.projectCase}`,
            isGroup: false,
            mapCase: entry.mapCase,
            mapCases: [entry.mapCase],
            displayName: entry.displayName,
            caseData,
        };
    });
    const isQueryable = ({ caseData }) => !caseData || caseDataHasFragments(caseData);

    const [selectedMapCases, setSelectedMapCases] = useState(() => {
        const set = new Set();
        // Auto-select loaded structures that have fragment data. Structures of
        // interest (not loaded) stay available to select in the grid, but start
        // UNchecked — the user opts them into a query rather than querying by
        // default.
        projectTargetItems.forEach(({ mapCase, caseData }) => {
            if (caseData && caseDataHasFragments(caseData)) set.add(mapCase);
        });
        return set;
    });
    const [fromAtom, setFromAtom] = useState(initialFromAtom);
    const [toAtom, setToAtom] = useState(initialToAtom);
    const [maxBValue, setMaxBValue] = useState(defaultMaxBValue);
    const [searchRadius, setSearchRadius] = useState(defaultSearchRadius);
    const [fragsetIds, setFragsetIds] = useState([]);
    const [fragmentNames, setFragmentNames] = useState([]);
    const [isRunning, setIsRunning] = useState(false);

    // Return early only if NOTHING is queryable (no loaded-with-fragments and no unloaded
    // structures of interest); disabled no-fragment rows alone shouldn't keep the form open.
    if (!projectTargetItems.some(isQueryable)) {
        const loadedCaseDatas = getTargetMapCases(targetList)
            .map((mapCase) => App.Workspace.lookupCaseData(mapCase))
            .filter(Boolean);
        if (determineDataState(loadedCaseDatas) === FragQueryDataStates.Initializing) {
            return (
                <FragMenuWrapper>
                    <FragMenuLoading>Waiting for fragment data availability...</FragMenuLoading>
                </FragMenuWrapper>
            );
        }
        return <FragMenuWrapper>No fragment data is available.</FragMenuWrapper>;
    }

    const runningMsg = anyUserData([...selectedMapCases])
        ? (
            <>
                Preparing and running fragment data query.
                <br />
                The first time could take a while...
            </>
        )
        : 'Running fragment data query...';
    const disableClassicQueries = !atomCaseDataHasFragments(fromAtom);
    const disableClassicGrow = !toAtom || disableClassicQueries;

    function updateMaxBValue(valueStr) {
        setMaxBValue(valueStr);
    }
    function handleBlurMaxBValue(valueStr) {
        setMaxBValue(valueStr === '' ? defaultMaxBValue : Number(valueStr));
    }
    function updateSearchRadius(valueStr) {
        setSearchRadius(valueStr);
    }
    function handleBlurSearchRadius(valueStr) {
        setSearchRadius(valueStr === '' ? defaultSearchRadius : Number(valueStr));
    }
    function updateFragsetIds(fsIds) {
        setFragsetIds(fsIds);
    }
    function updateFragmentNames(fragmentStr) {
        setFragmentNames(fragmentStr.split(/\s+/));
    }
    function clearFragmentFilters() {
        setFragmentNames([]);
        setFragsetIds([]);
    }
    function getFragset(fsid) {
        return fragData.fragsetById(fsid);
    }

    async function validate(callback) {
        if (selectedMapCases.size === 0) {
            return;
        }
        // Including a curated panel can select many structures; querying each is a server
        // round-trip, so confirm before running a large query.
        if (selectedMapCases.size > LargeQueryThreshold) {
            const proceed = await userConfirmation(
                'Query many structures?',
                `This will query fragment data across ${selectedMapCases.size} structures, `
                + 'which may take a while. Continue?',
            );
            if (!proceed) return;
        }
        callback();
    }

    async function runQuery(queryType='near') {
        setIsRunning(true);
        // Supports three search modes, distinguished by findType + (findNearAtom | findBondAtoms).
        // These attributes control both the dataservice query and the client-side filtering.
        // - near: 'Near' + findNearAtom: dataservice radius search, return all fragments as-is.
        // - vector: 'Near' + findBondAtoms: dataservice cone search, return all fragments as-is.
        // - grow: 'Bond' + findBondAtoms:  filter fragments per linker distance/angle
        //        geometry and remove clashes. findType defaults to 'Bond'.
        // When the time comes to add more link types and reaction awareness,
        // this query structure may need to be refactored.
        const { suggestions, errors } = await UserActions.FragDataQuery({
            maxBValue,
            searchRadius,
            findNearAtom: queryType === 'near' ? fromAtom : null,
            findBondAtoms: (queryType === 'vector' || queryType === 'grow') ? [fromAtom, toAtom] : null,
            findType: queryType === 'grow' ? 'Bond' : 'Near',
            mapCases: [...selectedMapCases],
            fragsets: fragsetIds.map((fsid) => getFragset(fsid)),
            fragmentNames,
        });

        // fragList is only needed for bfd-server results; dataservice uses getSvgForFragment
        const cmd = queryType === 'grow' ? 'grow' : 'near';
        EventBroker.publish('displaySuggestions', {
            suggestions, errors, sourceAtom: fromAtom, cmd, fragList: null,
        });
        close();
        setIsRunning(false);
    }

    async function runQueryClassic(doGrow=false) {
        setIsRunning(true);
        const scope = 'all';
        let suggestions;
        let errors;

        if (doGrow) {
            ({ suggestions, errors } = await UserActions.GrowFromAtom(
                [fromAtom, toAtom], 'Bond', scope,
            ));
        } else {
            ({ suggestions, errors } = await UserActions.SearchNear(
                fromAtom, scope, searchRadius,
            ));
        }

        const firstLoadedCaseData = [...selectedMapCases]
            .map((mc) => App.Workspace.lookupCaseData(mc))
            .find(Boolean);
        EventBroker.publish('displaySuggestions', {
            suggestions,
            errors,
            sourceAtom: fromAtom,
            cmd: doGrow ? 'grow' : 'near',
            fragList: firstLoadedCaseData?.getAvailableFragmentInfo() ?? null,
        });

        close();
        setIsRunning(false);
    }

    // Put the clicked-on structure first in the projects list so it's
    // obvious which one is the source of the bond vector being queried.
    const clickedCaseData = fromAtom ? App.getDataParents(fromAtom).caseData : null;
    // Clicking an atom means that case is loaded as its own individual structure, so match
    // on case identity — not collection membership, which could pick a different entry.
    const clickedItem = clickedCaseData
        ? projectTargetItems.find((i) => i.caseData === clickedCaseData)
        : null;
    const orderedItems = clickedItem
        ? [clickedItem, ...projectTargetItems.filter((i) => i !== clickedItem)]
        : projectTargetItems;

    const showProjectsSection = orderedItems.length > 0;

    // Summary shown in the Projects header: first-selected as a Chip,
    // then "+N other(s)" as caption text when more are selected.
    const firstSelected = orderedItems
        .find((i) => i.mapCases.some((mc) => selectedMapCases.has(mc)));
    const selectedEntryCount = orderedItems.filter(
        (i) => i.mapCases.some((mc) => selectedMapCases.has(mc))
    ).length;
    const otherSelectedCount = Math.max(0, selectedEntryCount - (firstSelected ? 1 : 0));
    const projectsSummary = firstSelected ? (
        <>
            <SummaryChip
                label={firstSelected.displayName ?? firstSelected.caseData?.getName()}
            />
            { otherSelectedCount > 0 && (
                <SummaryMore>
                    {`+${otherSelectedCount} ${pluralize(otherSelectedCount, 'other')}`}
                </SummaryMore>
            )}
        </>
    ) : (
        <SummaryPlaceholder>no projects selected</SummaryPlaceholder>
    );

    // Summary shown in the Filter-fragments header: a Chip for the first
    // frag name, a Chip for the first fragset, and "+N" tails for any rest.
    // Fragment names are already split by whitespace by updateFragmentNames.
    const fragTokens = fragmentNames.map((s) => s.trim()).filter(Boolean);
    const firstFragName = fragTokens[0];
    const otherFragCount = Math.max(0, fragTokens.length - 1);
    const firstFragSetId = fragsetIds[0];
    const firstFragSet = firstFragSetId ? getFragset(firstFragSetId) : null;
    const otherFragSetCount = Math.max(0, fragsetIds.length - 1);
    const hasAnyFilter = !!(firstFragName || firstFragSet);
    const fragmentsSummary = hasAnyFilter ? (
        <>
            { firstFragName && <SummaryChip label={firstFragName} /> }
            { firstFragName && otherFragCount > 0 && (
                <SummaryMore>
                    {`+${otherFragCount} ${pluralize(otherFragCount, 'frag')}`}
                </SummaryMore>
            )}
            { firstFragSet && (
                <SummaryChip label={firstFragSet.name} variant="outlined" />
            )}
            { firstFragSet && otherFragSetCount > 0 && (
                <SummaryMore>
                    {`+${otherFragSetCount} ${pluralize(otherFragSetCount, 'fragset')}`}
                </SummaryMore>
            )}
        </>
    ) : (
        <SummaryChip label="include all fragments" />
    );

    return (
        <FragMenuWrapper>
            {/* Scrollable middle. `flex: 1 1 auto` lets it absorb any
                extra height when the wrapper's minHeight floor exceeds
                the natural content size, so the footer stays anchored
                to the bottom instead of floating mid-panel. */}
            <Box sx={{
                flex: '1 1 auto',
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0,
            }}
            >
                { showProjectsSection && (
                    <CollapsibleSection title="Projects to search" summary={projectsSummary}>
                        <TargetControl
                            items={orderedItems}
                            selectedMapCases={selectedMapCases}
                            onSelectionChange={setSelectedMapCases}
                        />
                    </CollapsibleSection>
                )}
                <CollapsibleSection
                    title="Fragment Filters"
                    summary={fragmentsSummary}
                    headerEnd={hasAnyFilter && (
                        <BMapsTooltip title="Clear fragment filters">
                            <IconButton
                                size="small"
                                onClick={clearFragmentFilters}
                                aria-label="Clear fragment filters"
                            >
                                <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                        </BMapsTooltip>
                    )}
                >
                    <FragmentsFilter
                        fragData={fragData}
                        fragsetIds={fragsetIds}
                        onChangeFragsetIds={updateFragsetIds}
                        fragmentNames={fragmentNames}
                        onChangeFragmentNames={updateFragmentNames}
                    />
                </CollapsibleSection>
                <CollapsibleSection title="Other Filters" collapsible={false}>
                    <OtherFilters
                        maxBValue={maxBValue}
                        searchRadius={searchRadius}
                        onChangeMaxBValue={updateMaxBValue}
                        onBlurMaxBValue={handleBlurMaxBValue}
                        onChangeSearchRadius={updateSearchRadius}
                        onBlurSearchRadius={handleBlurSearchRadius}
                    />
                </CollapsibleSection>
            </Box>
            {/* Action footer: a non-shrinking flex item directly
                under the scrollable middle. The wrapper is
                content-sized (with a maxHeight cap), so the footer
                sits at the visible bottom of the panel regardless of
                how many collapsibles are open. */}
            <Box
                sx={{
                    flex: '0 0 auto',
                    pt: 0.5,
                    mt: 0.5,
                    borderTop: 1,
                    borderColor: 'divider',
                }}
            >
                { !isRunning ? (
                    <>
                        <FragMenuButton
                            onClick={() => validate(() => runQuery('near'))}
                            label="Search Nearby"
                        />
                        <FragMenuButton
                            onClick={() => validate(() => runQuery('vector'))}
                            disabled={!toAtom}
                            label="Vector Search"
                        />
                        <FragMenuButton
                            onClick={() => validate(() => runQuery('grow'))}
                            disabled={!toAtom}
                            label="Fragment Grow"
                        />
                        <Divider sx={{ my: 0.5 }} textAlign="center">
                            <BMapsTooltip title="Classic searches only &quot;searchable&quot; fragment simulation data loaded for the target structure">
                                <Typography variant="caption" color="text.secondary">
                                    Classic search
                                </Typography>
                            </BMapsTooltip>
                        </Divider>
                        <FragMenuButton
                            onClick={() => validate(() => runQueryClassic(false))}
                            disabled={disableClassicQueries}
                            disabledTooltip={disableClassicQueries ? ClassicNoFragmentDataTooltip : ''}
                            label="Search Nearby (classic)"
                        />
                        <FragMenuButton
                            onClick={() => validate(() => runQueryClassic(true))}
                            disabled={disableClassicGrow}
                            disabledTooltip={disableClassicQueries ? ClassicNoFragmentDataTooltip : ''}
                            label="Fragment Grow (classic)"
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}
                        >
                            Note: &quot;Classic&quot; searches will only use the Search Radius,
                            not the other search options.
                        </Typography>
                    </>
                ) : (
                    <FragMenuLoading>{runningMsg}</FragMenuLoading>
                )}
            </Box>
        </FragMenuWrapper>
    );
}

function TargetControl({ items, selectedMapCases, onSelectionChange }) {
    const error = selectedMapCases.size === 0;

    // MapCases covered by a currently-selected group (library): an individual row for one of
    // these can't be deselected, because selecting the library includes it regardless.
    const lockedByGroup = new Set();
    items.forEach((item) => {
        if (item.isGroup && item.mapCases.every((mc) => selectedMapCases.has(mc))) {
            item.mapCases.forEach((mc) => lockedByGroup.add(mc));
        }
    });

    const rows = items.map((item) => {
        // The stable row id is computed upstream: a curated panel uses its groupId; an
        // individual uses `sourceDesc:uri` (uri carries the mount index/scheme; projectCase
        // is '' for imports, so it would collide across imported structures).
        const { hasFragments, loading } = item.caseData
            ? caseDataFragmentAvailability(item.caseData)
            : { hasFragments: true, loading: false };
        const lockedByLibrary = !item.isGroup && item.mapCases.some((mc) => lockedByGroup.has(mc));
        const baseName = item.isGroup
            ? `${item.displayName} (${item.mapCases.length})`
            : (item.displayName ?? item.caseData?.getName() ?? item.mapCase?.displayName);
        return {
            id: item.id,
            mapCases: item.mapCases,
            displayName: lockedByLibrary ? `${baseName} (in selected panel)` : baseName,
            hasFragments,
            loading,
            lockedByLibrary,
        };
    });

    // Toggle a row's selection by clicking its name (not only the checkbox). No-op for rows
    // that aren't selectable (no fragment data, or locked because their library is selected).
    function toggleRowSelection(row) {
        if (!row.hasFragments || row.lockedByLibrary) return;
        const next = new Set(selectedMapCases);
        const allSelected = row.mapCases.every((mc) => next.has(mc));
        row.mapCases.forEach((mc) => (allSelected ? next.delete(mc) : next.add(mc)));
        onSelectionChange(next);
    }

    const columns = [{
        field: 'displayName',
        headerName: 'Structure',
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
            const clickable = row.hasFragments && !row.lockedByLibrary;
            const label = row.hasFragments ? row.displayName : (
                <BMapsTooltip title={row.loading
                    ? 'Determining available fragments for this structure...'
                    : 'No fragment simulation data for this structure.'}
                >
                    <span style={{ opacity: 0.5 }}>{row.displayName}</span>
                </BMapsTooltip>
            );
            if (!clickable) return label;
            return (
                <button
                    type="button"
                    onClick={() => toggleRowSelection(row)}
                    style={{
                        cursor: 'pointer',
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        color: 'inherit',
                    }}
                >
                    {label}
                </button>
            );
        },
    }];

    // DataGrid v7 controlled selection requires the { type, ids: Set } object form.
    // A group row is checked only when ALL its MapCases are selected.
    const selectionModel = {
        type: 'include',
        ids: new Set(rows
            .filter((r) => r.mapCases.every((mc) => selectedMapCases.has(mc)))
            .map((r) => r.id)),
    };

    function handleSelectionChange(model) {
        // "select all" reports { type: 'exclude', ids: <empty Set> } (nothing excluded, i.e.
        // everything selected) rather than listing every id, so membership must be checked
        // against the resolved selection, not `model.ids` itself.
        const isSelected = (id) => (model.type === 'exclude' ? !model.ids.has(id) : model.ids.has(id));
        const newSelected = new Set();
        rows.forEach((r) => {
            if (isSelected(r.id)) {
                r.mapCases.forEach((mc) => newSelected.add(mc));
            }
        });
        onSelectionChange(newSelected);
    }

    return (
        <>
            <DataGrid
                rows={rows}
                columns={columns}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={handleSelectionChange}
                isRowSelectable={(params) => params.row.hasFragments && !params.row.lockedByLibrary}
                pageSizeOptions={[5, 10, 15]}
                showToolbar
                initialState={{
                    density: 'compact',
                    pagination: { paginationModel: { pageSize: 5, page: 0 } },
                }}
                slots={{
                    toolbar: TargetToolbar,
                    footer: TargetFooter,
                }}
                columnHeaderHeight={0}
                disableColumnMenu
                onKeyDown={stopPropagation}
                sx={{ border: 0 }}
            />
            {error && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                    Select at least one structure
                </Typography>
            )}
        </>
    );
}

/**
 *
 * @param {{
 *      caseDatas: [CaseData],
 *      fragData: import('BMapsModel').FragmentData,
 *      maxBValue: number,
 *      onChangeMaxBValue: function,
 *      searchRadius: number,
 *      onChangeSearchRadius: function
 * }} param0
 * @returns
 */
function FragmentsFilter({
    fragData,
    fragsetIds, onChangeFragsetIds,
    fragmentNames, onChangeFragmentNames,
}) {
    // Section heading is provided by the parent CollapsibleSection.
    const fragSets = fragData.getFragsets();
    const value = fragsetIds;
    return (
        <>
            <FragmentNames
                fragmentNames={fragmentNames}
                onChangeFragmentNames={onChangeFragmentNames}
            />
            { fragSets.length > 0 && (
                <div style={{ marginTop: '0.5em' }}>
                    <div>Fragment sets</div>
                    <Select
                        value={value}
                        onChange={
                            (elt) => onChangeFragsetIds(
                                [...elt.target.selectedOptions].map((option) => option.value)
                            )
                        }
                        onKeyDown={stopPropagation}
                        slotProps={{
                            htmlInput: {
                                multiple: true,
                                size: Math.min(3, fragSets.length),
                                style: { padding: '0.2em' },
                            },
                        }}
                    >
                        { fragSets.map((fs) => (
                            <option key={fs.name} value={fs.fragsetId}>{fs.name}</option>
                        ))}
                    </Select>
                </div>
            )}
        </>
    );
}

// HELPER FUNCTIONS

function stopPropagation(evt) {
    evt.stopPropagation();
}

/**
 * Fragment-data availability for a single structure. Single source of truth
 * for the "structure has fragment data" condition shared by the disabled
 * classic-search buttons (#936) and the disabled project checkboxes.
 * @param {import('BMapsModel').CaseData} caseData
 * @returns {{ hasFragments: boolean, loading: boolean }}
 *   hasFragments: fragment data is present and queryable.
 *   loading: no fragments yet AND the availability list hasn't arrived
 *     (distinguishes "still determining" from "arrived, genuinely empty").
 */
function caseDataFragmentAvailability(caseData) {
    const fragList = caseData.getAvailableFragmentInfo();
    if (fragList.items().length > 0) return { hasFragments: true, loading: false };
    return { hasFragments: false, loading: !fragList.isInitialized() };
}

function caseDataHasFragments(caseData) {
    return caseDataFragmentAvailability(caseData).hasFragments;
}

function atomCaseDataHasFragments(atom) {
    const { caseData } = App.getDataParents(atom);
    return !!(caseData && caseDataHasFragments(caseData));
}

/**
 * Check if we are waiting for information about fragment data to arrive
 * @param {CaseData[]} caseDatas
 */
function determineDataState(caseDatas) {
    const usableCaseDatas = caseDatas.filter((caseData) => caseData.mapCase);
    if (usableCaseDatas.length === 0) return FragQueryDataStates.NoProtein;

    const availabilities = usableCaseDatas.map(caseDataFragmentAvailability);
    if (availabilities.some(({ hasFragments }) => hasFragments)) {
        return FragQueryDataStates.DataAvailable;
    }
    if (availabilities.every(({ loading }) => !loading)) {
        return FragQueryDataStates.NoDataAvailable;
    }
    return FragQueryDataStates.Initializing;
}

/**
 * Does any of our target fragment data contain user data that might need to be compressed?
 * @param {import('BMapsSrc/model').MapCase[]} mapCases
 * @returns {boolean}
 */
function anyUserData(mapCases) {
    // Public + managed (curated server datasets, e.g. the kinase library) are curated
    // server libraries; everything else (imports, fragserv, folding) is user-supplied
    // data that may need compression.
    const curatedSources = [MapCase.Sources.Public, MapCase.Sources.Managed];
    return mapCases.some((mc) => {
        if (!curatedSources.includes(mc.sourceDesc)) return true;
        // hasUserSeries lives on the loaded fragment info; an unloaded structure of
        // interest has none available client-side, so it can't carry user series here.
        const caseData = App.Workspace.lookupCaseData(mc);
        return caseData?.getAvailableFragmentInfo()?.hasUserSeries ?? false;
    });
}

function pluralize(n, singular, plural=`${singular}s`) {
    return n === 1 ? singular : plural;
}

// HELPER COMPONENTS

// Width of the triangulation guard. Enough cover for typical diagonal
// mouse paths from a parent menu item back into the upper part of an
// expanded FDQ panel without closing the submenu.
const TRIANGULATION_GUARD_PX = 100;

function FragMenuWrapper({ children }) {
    // The MUI Popover Paper that hosts this panel is repurposed as an
    // invisible positioning container (transparent background, no shadow,
    // overflow: visible). The visible panel is the wrapper, with its own
    // paper visuals. Two invisible guards extend the hover area beyond
    // the wrapper so the parent NestedMenuItem doesn't fire mouseLeave:
    //
    //   - guardTop covers the area where the panel used to be when it
    //     was at its maximum expanded height. As the wrapper shrinks
    //     (the user collapsed a section), the guard grows to fill the
    //     gap. Sized by `usePinPopoverBottom`.
    //   - guardLeft covers a triangulation strip to the left of the
    //     panel so diagonal mouse moves back toward the parent menu
    //     don't fall through into "no element".
    //
    // `usePinPopoverBottom` also flips the Paper from MUI's default
    // top-anchored placement to a fixed-bottom layout, so the panel
    // grows upward when content expands instead of jumping after MUI
    // re-runs setPositioningStyles.
    //
    // Stop mousedown/click from bubbling to the parent NestedMenuItem;
    // its MUI ButtonBase otherwise fires a ripple animation every time
    // you click a form control inside this panel.
    const swallowMouse = (e) => e.stopPropagation();
    const wrapperRef = useRef(null);
    const guardTopRef = useRef(null);
    usePinPopoverBottom(wrapperRef, guardTopRef);
    return (
        <div style={{ position: 'relative' }}>
            <div
                ref={guardTopRef}
                style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    height: 0,
                    pointerEvents: 'auto',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: '100%',
                    width: TRIANGULATION_GUARD_PX,
                    pointerEvents: 'auto',
                }}
            />
            <Typography
                component="div"
                ref={wrapperRef}
                onMouseDown={swallowMouse}
                onClick={swallowMouse}
                sx={{
                    // Padding (not margin): the wrapper now supplies
                    // its own bgcolor/shadow/borderRadius, so the
                    // inset belongs inside the visible panel, not as
                    // transparent space around it.
                    p: '.2em .5em',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 'clamp(400px, 32vw, 600px)',
                    minHeight: 'clamp(300px, 50vh, 600px)',
                    maxHeight: '85vh',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: 8,
                    borderRadius: 1,
                }}
            >
                <div style={{ fontWeight: 'bold', textAlign: 'center', flexShrink: 0 }}>
                    Fragment Data Query
                </div>
                {children}
            </Typography>
        </div>
    );
}

/** Small chip used in collapsed-section summaries. */
function SummaryChip({ label, variant='filled' }) {
    return (
        <Chip
            size="small"
            variant={variant}
            label={label}
            sx={{
                height: 20,
                maxWidth: '100%',
                '& .MuiChip-label': { fontSize: '0.75rem', px: 1 },
            }}
        />
    );
}

/** "+N more" caption next to the SummaryChip(s). */
function SummaryMore({ children }) {
    return (
        <Typography variant="caption" color="text.secondary" noWrap>
            {children}
        </Typography>
    );
}

/** Italic placeholder when nothing is selected. */
function SummaryPlaceholder({ children }) {
    return (
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
            noWrap
        >
            {children}
        </Typography>
    );
}

/**
 * Section with a header that may optionally collapse its content. Drives
 * its own expand state so a `summary` (string or JSX) can render in the
 * header. Used for collapsible sections (Projects, Fragment Filters) and
 * as a static section (Other Filters) when `collapsible={false}`.
 */
function CollapsibleSection({
    title, summary, headerEnd, defaultExpanded=false, collapsible=true, children,
}) {
    const [collapsibleExpanded, setExpanded] = useState(defaultExpanded);
    const expanded = collapsible ? collapsibleExpanded : true;
    function toggle() { if (collapsible) setExpanded((e) => !e); }
    function handleKeyUp(e) {
        if (!collapsible) return;
        if (e.nativeEvent.code === 'Space' || e.nativeEvent.code === 'Enter') toggle();
    }
    // width: 100% + box-sizing: border-box gives the hover background full
    // width without the negative-margin trick (which produced a horizontal
    // scrollbar in the middle scroll Box).
    const headerInteractive = collapsible ? {
        onClick: toggle,
        onKeyUp: handleKeyUp,
        role: 'button',
        'aria-expanded': expanded,
        tabIndex: 0,
    } : {};
    const headerSx = {
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 1,
        px: 0.5,
        py: 0.25,
        ...(collapsible && {
            cursor: 'pointer',
            transition: (theme) => theme.transitions.create('background-color', {
                duration: theme.transitions.duration.shortest,
            }),
            '&:hover': { bgcolor: 'action.hover' },
            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
            },
        }),
    };
    const summaryNode = (typeof summary === 'string')
        ? (
            <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ width: '100%' }}
            >
                {summary}
            </Typography>
        )
        : summary;
    return (
        <Box sx={{ py: 0.5 }}>
            <Box {...headerInteractive} sx={headerSx}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flexShrink: 0 }}>
                    {title}
                </Typography>
                {!!summary && (
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        {summaryNode}
                    </Box>
                )}
                {!!headerEnd && (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        {headerEnd}
                    </Box>
                )}
                {collapsible && (
                    <Box sx={{
                        ml: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0,
                    }}
                    >
                        {expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    </Box>
                )}
            </Box>
            {collapsible ? (
                <Collapse in={expanded}><Box>{children}</Box></Collapse>
            ) : (
                <Box>{children}</Box>
            )}
        </Box>
    );
}

// Note this is a separate component in case we want to do dynamic filtering by frag name
function FragmentNames({ fragmentNames, onChangeFragmentNames }) {
    return (
        <div>
            <div>Fragment name</div>
            <TextArea
                value={fragmentNames.join('\n')}
                onChange={(elt) => onChangeFragmentNames(elt.target.value)}
                onKeyDown={stopPropagation}
            />
        </div>
    );
}

function OtherFilters({
    maxBValue, onChangeMaxBValue, onBlurMaxBValue,
    searchRadius, onChangeSearchRadius, onBlurSearchRadius,
}) {
    // Section heading provided by the parent CollapsibleSection (with
    // collapsible={false}) so all three section headings share visuals.
    return (
        <>
            <FragMenuTextField
                label="Search Radius"
                type="number"
                value={searchRadius}
                onChange={(elt) => onChangeSearchRadius(elt.target.value)}
                onBlur={(elt) => onBlurSearchRadius(elt.target.value)}
            />
            <FragMenuTextField
                label="Max BValue"
                type="number"
                value={maxBValue}
                onChange={(elt) => onChangeMaxBValue(elt.target.value)}
                onBlur={(elt) => onBlurMaxBValue(elt.target.value)}
            />
        </>
    );
}

function FragMenuTextField({
    label, id, value, onChange, disabled, type='text', ...rest
}) {
    return (
        <TextField
            fullWidth
            variant="outlined"
            label={label}
            id={id}
            style={{ margin: '10px 0' }}
            value={value}
            onChange={(evt) => onChange(evt)}
            disabled={disabled}
            type={type}
            required
            onKeyDown={stopPropagation}
            {...rest}
        />
    );
}

function FragMenuButton({
    onClick, disabled, disabledTooltip='', label,
}) {
    const button = (
        <DialogSubmitButton
            style={{ width: '100%', marginBottom: '0.25em' }}
            onClick={onClick}
            disabled={disabled}
            onKeyDown={stopPropagation}
        >
            {label}
        </DialogSubmitButton>
    );
    if (!disabled || !disabledTooltip) return button;
    return (
        <BMapsTooltip title={disabledTooltip}>
            {wrapForDisabledTooltip(button)}
        </BMapsTooltip>
    );
}

function FragMenuLoading({ children }) {
    // Generous vertical padding so the panel doesn't dramatically
    // shrink when the running state replaces the action footer
    // (5 buttons + divider + note collapsing to a one-line message
    // would otherwise yank ~200px off the panel height).
    return (
        <Box sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
            { children }
            <WorkingIndicator />
        </Box>
    );
}

function TargetToolbar(props) {
    return (
        <GridToolbarContainer {...props}>
            {/* Stop keydown from reaching DataGrid's cell-navigation handlers */}
            <div
                role="presentation"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    overflow: 'hidden',
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <GridToolbarQuickFilter style={{ width: '100%' }} />
                </div>
                <DensityIconButton />
            </div>
        </GridToolbarContainer>
    );
}

function DensityIconButton() {
    const apiRef = useGridApiContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const density = useGridSelector(apiRef, gridDensitySelector);

    const openMenu = (event) => setAnchorEl(event.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    const setDensity = (value) => {
        apiRef.current.setDensity(value);
        closeMenu();
    };

    const icon = useMemo(() => {
        switch (density) {
            case 'compact':
                return <GridViewHeadlineIcon />;
            case 'comfortable':
                return <GridViewStreamIcon />;
            case 'standard':
            default:
                return <GridTableRowsIcon />;
        }
    }, [density]);

    return (
        <>
            <BMapsTooltip title="Table Density">
                <IconButton aria-label="Table Density" onClick={openMenu}>
                    {icon}
                </IconButton>
            </BMapsTooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                <MenuItem onClick={() => setDensity('compact')}>Compact</MenuItem>
                <MenuItem onClick={() => setDensity('standard')}>Standard</MenuItem>
                <MenuItem onClick={() => setDensity('comfortable')}>Comfortable</MenuItem>
            </Menu>
        </>
    );
}

function TargetFooter() {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const { page } = useGridSelector(apiRef, gridPaginationModelSelector);

    if (pageCount <= 1) return null;

    return (
        <MuiPagination
            count={pageCount}
            page={page + 1}
            onChange={(_, value) => apiRef.current.setPage(value - 1)}
            size="small"
            sx={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}
        />
    );
}
